import {useMediaQuery} from '@chakra-ui/react';
import {unwrapResult} from '@reduxjs/toolkit';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiBox,
  UiButton,
  UiGridItem,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
} from '~/components/elements';
import {QUERY_MOBILE} from '~/constants';
import {MENSTRUAL_HEALTH_KEYS, METHOD_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {Option} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire9Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const {addMessage} = useNotification(dispatch);

  const {loading} = useRequestState(addQuestionnaire);
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[MENSTRUAL_HEALTH_KEYS.HOW_TO_DEAL]?.data,
  );

  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const NONE_VALUE = 'NONE';

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === MENSTRUAL_HEALTH_KEYS.HOW_TO_DEAL);

  let medicalOptions: Option[] = [];
  if (questionnaire?.options?.length) {
    medicalOptions = questionnaire?.options?.filter(i => i?.value !== NONE_VALUE);
  }

  const defaultOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    if (currentAnswer?.selectedOption?.length) {
      currentAnswer?.selectedOption.map((i: string | number) => {
        if (i !== NONE_VALUE) res[i] = true;
      });
    }

    return res;
  }, {});

  const resetOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    return res;
  }, {});

  const {inputs, setInputs, validation} = useInput({
    defaultOptions,
    noneOptions: (currentAnswer?.selectedOption?.length && currentAnswer?.selectedOption[0] === NONE_VALUE) || false,
  });
  const dataSubmit = Object.keys(inputs.defaultOptions).filter(i => inputs.defaultOptions[i] === true);

  const onClickOption = (value: string | number) => {
    if (value === NONE_VALUE) {
      setInputs({noneOptions: !inputs.noneOptions, defaultOptions: resetOptions});
    } else {
      setInputs({
        noneOptions: false,
        defaultOptions: {...inputs.defaultOptions, [value]: !inputs.defaultOptions[value]},
      });
    }
  };

  const onClickSubmit = async () => {
    const questionnaireAnswer = {
      key: MENSTRUAL_HEALTH_KEYS.HOW_TO_DEAL,
      index: questionnaire?.index || '',
      data: {selectedOption: inputs?.noneOptions ? [NONE_VALUE] : dataSubmit},
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));

    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...medicalQuestionnaireAnswer,
            [MENSTRUAL_HEALTH_KEYS.HOW_TO_DEAL]: {...questionnaireAnswer},
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: METHOD_KEYS.MENSTRUAL_HEALTH,
        },
      }),
    );

    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      if (result?.booking) {
        const type = result?.slotTime;
        dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
        history.push(`/appointment?type=${type}&methodKey=${METHOD_KEYS.MENSTRUAL_HEALTH}&token=${savedToken}`);
      }
    }
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!dataSubmit.length && !inputs.noneOptions}
      handleOnNext={onClickSubmit}
      loading={loading}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid
            templateColumns="repeat(4,1fr)"
            gap={4}
            mt={spacing.smedium}
            px="5%"
            minChildWidth={spacing.big}
            width="100%"
          >
            {questionnaire?.options?.map((i, index: number) => {
              return (
                <UiGridItem
                  key={i.value}
                  colSpan={isDesktop ? 2 : 4}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <UiButton
                    key={`${i.label}-${i.value}`}
                    title={i.label}
                    variant={
                      inputs.defaultOptions[i.value] || (i.value === NONE_VALUE && inputs.noneOptions)
                        ? 'solid'
                        : 'toggle'
                    }
                    onClick={() => onClickOption(i.value)}
                    minWidth="40%"
                    width={'100%'}
                    height={spacing.standard}
                    px={5}
                  />
                </UiGridItem>
              );
            })}
          </UiSimpleGrid>
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire9Screen;
